import React, { useState, useEffect, useRef } from 'react';
import {
  Editor,
  EditorState,
  RichUtils,
  Modifier,
  convertToRaw,
  AtomicBlockUtils,
} from 'draft-js';
import { Button } from "../../components/ui/button";
import 'boxicons';
import { Packer, Paragraph, TextRun, Document, ImageRun } from 'docx';
import {
    Select,
    SelectValue,
    SelectTrigger,
    SelectContent,
    SelectItem,
  } from "../../components/ui/select"
import '../../styles/common/TextEditor.css'

const blockRenderer = (contentBlock) => {
  if (contentBlock.getType() === 'atomic') {
    return {
      component: ImageComponent,
      editable: false,
    };
  }
  return null;
};

const blockStyleFn = (contentBlock) => {
  let alignment = 'left';
  contentBlock.findStyleRanges((e) => {
      if (e.hasStyle('center')) {
        alignment = 'center';
      }
      if (e.hasStyle('right')) {
        alignment = 'right';
      }
    });
    return `editor-alignment-${alignment}`;
};

const ImageComponent = (props) => {
  const entity = props.contentState.getEntity(props.block.getEntityAt(0));
  const { src } = entity.getData();
  return (
    <img src={src} style={{ maxWidth: '1000px' }} />
  );
};

export default function TextEditor({ insertedImage }) {
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const insertedCount = useRef(0);
  {/* Text add by users inside the Editor to export*/}
  const [inputText, setInputText] = useState();
  {/* IMG add by users inside the Editor to export*/}
  var [iconElements, setIconElements] = useState([]);
  const [pressedBold, setPressedBold] = useState(false);
  const [pressedItalic, setPressedItalic] = useState(false);
  const [pressedUnderline, setPressedUnderline] = useState(false);
  const [pressedAlignL, setPressedAlignL] = useState(false);
  const [pressedAlignM, setPressedAlignM] = useState(false);
  const [pressedAlignR, setPressedAlignR] = useState(false);
  {/* Setting command handlers */}
  const handleKeyCommand = (command, editorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);

    switch (command) {
      case 'bold':
        toggleBoldPressedMode();
        break;
      case 'italic':
        toggleItalicPressedMode();
        break;
      case 'underline':
        toggleUnderlinePressedMode();
        break;
      default:
        break;
    }

    if (newState) {
      setEditorState(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  const toggleBoldPressedMode = () => {
    setPressedBold(!pressedBold);
  }

  const toggleItalicPressedMode = () => {
    setPressedItalic(!pressedItalic);
  }

  const toggleUnderlinePressedMode = () => {
    setPressedUnderline(!pressedUnderline);
  }

  const toggleAlignLPressedMode = () => {
    setPressedAlignM(false);
    setPressedAlignR(false);
    setPressedAlignL(!pressedAlignL);
  }

  const toggleAlignMPressedMode = () => {
    setPressedAlignL(false);
    setPressedAlignR(false);
    setPressedAlignM(!pressedAlignM);
  }

  const toggleAlignRPressedMode = () => {
    setPressedAlignL(false);
    setPressedAlignM(false);
    setPressedAlignR(!pressedAlignR);
  }

  const alignmentStyles = ['left', 'right', 'center'];
  const toggleAlignment = (newStyle) => {
    let styleForRemove = alignmentStyles.filter(style => style !== newStyle);
    let currentContent = editorState.getCurrentContent();
    let selection = editorState.getSelection();
    let focusBlock = currentContent.getBlockForKey(selection.getFocusKey());
    let anchorBlock = currentContent.getBlockForKey(selection.getAnchorKey());
    let isBackward = selection.getIsBackward();

    let selectionMerge = {
      anchorOffset: 0,
      focusOffset: focusBlock.getLength(),
    };

    if (isBackward) {
      selectionMerge.anchorOffset = anchorBlock.getLength();
    }
    
    let finalSelection = selection.merge(selectionMerge);
    let finalContent = styleForRemove.reduce((content, style) => Modifier.removeInlineStyle(content, finalSelection, style), currentContent);
    let modifiedContent = Modifier.applyInlineStyle(finalContent, finalSelection, newStyle);
    const nextEditorState = EditorState.push(editorState, modifiedContent, 'change-inline-style');
    setEditorState(nextEditorState);
  };
  
  const toggleBlockType = (blockType) => {
    setEditorState(RichUtils.toggleBlockType(editorState, blockType));
  };

  const toggleInlineStyle = (inlineStyle) => {
    setEditorState(RichUtils.toggleInlineStyle(editorState, inlineStyle));
  };

  {/* Text add by users inside the Editor to export*/}
  const handleInputChange = (e) => {
    setInputText(e.target.value);
  };

  const insertImage = (state, src) => {
    const content = state.getCurrentContent();
    const contentWithEntity = content.createEntity('IMAGE', 'IMMUTABLE', { src });
    const entityKey = contentWithEntity.getLastCreatedEntityKey();
    return AtomicBlockUtils.insertAtomicBlock(
      EditorState.set(state, { currentContent: contentWithEntity }),
      entityKey,
      ' '
    );
  };

  useEffect(() => {
    // Only process new URLs in insertedImage
    const newImages = insertedImage.slice(insertedCount.current);
    if (newImages.length === 0) return;

    // Chain each insertion over the last state
    let nextState = editorState;
    newImages.forEach(src => {
      nextState = insertImage(nextState, src);
    });

    setEditorState(nextState);
    insertedCount.current = insertedImage.length;
  }, [insertedImage]);
  

  {/* Generates a document with 2 types of structures (paragraphs & images) */}
  const generateDocument = async () => {

    const rawContent = convertToRaw(editorState.getCurrentContent());
    const { blocks, entityMap } = rawContent;

    // Counters for numbering references for each list type
    let orderedListCounter = 0;
    let unorderedListCounter = 0;
    // Track the last processed block type (to detect when a new list starts)
    let lastListType = null;

    const docChildren = blocks.map((block) => {
      if (block.type === 'atomic') {
        const entityKey = block.entityRanges[0]?.key;
        if (entityKey !== undefined && entityMap[entityKey].type === 'IMAGE') {
          const { src, width, height } = entityMap[entityKey].data;
          console.log("[DEBUG] Source info: ", src);
          console.log("[DEBUG] Dimensions: ", width, height);

          return new Paragraph({
            children: [
              new ImageRun({
                data: src,
                transformation: { width: width || 400, height: height || 300},
              }),
            ],
          });
        }
        return null;
      } else if (block.text.trim() !== '') {
        let docxStyle = undefined;

        console.log("[DEBUG] Block.inlineStyleRanges:", block.inlineStyleRanges);
        console.log("[DEBUG] Block.type: ", block.type);

        switch (block.type) {
          case 'header-one':
            docxStyle = "Heading1";
            break;
          case 'header-two':
            docxStyle = "Heading2";
            break;
          case 'header-three':
            docxStyle = "Heading3";
            break;
          case 'header-four':
            docxStyle = "Heading4";
            break;
          case 'header-five':
            docxStyle = "Heading5";
            break;
          case 'header-six':
            docxStyle = "Heading6";
            break;
          case 'unordered-list-item':
            docxStyle = "unordered-list-item";
            break;
          case 'ordered-list-item':
            docxStyle = "ordered-list-item"; 
            break;
          default:
            docxStyle = undefined; // Regular text (paragraph)
        }

        console.log('[DEBUG] DocXStyle:', docxStyle);
        
        if ( docxStyle !== "unordered-list-item" &&  docxStyle !== "ordered-list-item") {
          lastListType = null;

          return new Paragraph({
            alignment: block.inlineStyleRanges.some(range => range.style === "right") ? "right" : "" || block.inlineStyleRanges.some(range => range.style === "center") ? "center" : "" | block.inlineStyleRanges.some(range => range.style === "left") ? "left" : "",
            heading: docxStyle,
            children: [
              new TextRun({
                text: block.text,
                bold: block.inlineStyleRanges.some(range => range.style === "BOLD"),
                italics: block.inlineStyleRanges.some(range => range.style === "ITALIC"),
                underline: block.inlineStyleRanges.some(range => range.style === "UNDERLINE"),
              }),
            ],
          });     
        } else {
          // Process list items.
          let numberingReference = "";
          if (docxStyle === "ordered-list-item") {
            // If the last block wasn't an ordered list item, start a new list.
            if (lastListType !== "ordered-list-item") {
              orderedListCounter++;
            }
            numberingReference = "myOrderedList" + orderedListCounter;
            lastListType = "ordered-list-item";
          } else if (docxStyle === "unordered-list-item") {
            if (lastListType !== "unordered-list-item") {
              unorderedListCounter++;
            }
            numberingReference = "myUnOrderedList" + unorderedListCounter;
            lastListType = "unordered-list-item";
          }

          return new Paragraph({
            alignment: block.inlineStyleRanges.some(range => range.style === "right") ? "right" : "" || block.inlineStyleRanges.some(range => range.style === "center") ? "center" : "" | block.inlineStyleRanges.some(range => range.style === "left") ? "left" : "",
            heading: docxStyle,
            numbering: {
              reference: numberingReference,
              level: 0,
            },
            children: [
              new TextRun({
                text: block.text,
                bold: block.inlineStyleRanges.some(range => range.style === "BOLD"),
                italics: block.inlineStyleRanges.some(range => range.style === "ITALIC"),
                underline: block.inlineStyleRanges.some(range => range.style === "UNDERLINE"),
              }),
            ],
          });     
        }
      }
      
      return null;
    }).filter(item => item !== null);

    // Build numbering configurations based on the counters
    const numberingConfigs = [];

    // Create config objects for ordered lists
    for (let i = 1; i <= orderedListCounter; i++) {
      numberingConfigs.push({
        reference: "myOrderedList" + i,
        levels: [
          {
            level: 0,
            format: "decimal",
            text: "%1.",
            alignment: "left",
          },
        ],
      });
    }
    
    // Create config objects for unordered lists
    for (let j = 1; j <= unorderedListCounter; j++) {
      numberingConfigs.push({
        reference: "myUnOrderedList" + j,
        levels: [
          {
            level: 0,
            format: "bullet",
            text: "•",
            alignment: "left",
          },
        ],
      });
    }

    const doc = new Document({
      numbering: {
        config: numberingConfigs,
      },
      sections: [
        {
          properties: {},
          children: docChildren,
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "Report.docx";
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div id="editorDiv" className='editorDiv w-[1050px]' style={{
      border: '3px solid #ccc',
      padding: '10px',
      margin: '20px',
      borderRadius: '10px',
      minHeight: '1200px',
    }}>
      {/* Tool bar for block styles */}
      <div style={{ marginBottom: '5px',
        margin: "10px"
      }}>
        <div className='flex flex-row gap-4'>
          {/* Selector for type of text*/}
          <Select>
          <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Normal" />
          </SelectTrigger>
          <SelectContent>
              <SelectItem value="title1" onMouseDown={(e) => { e.preventDefault(); toggleBlockType('header-one'); }}
            className="font-extrabold" style={{fontSize: '20px'}}> Title 1</SelectItem>
              <SelectItem value="title2" onMouseDown={(e) => { e.preventDefault(); toggleBlockType('header-two'); }}
            className="font-extrabold" style={{fontSize: '18px'}}>Title 2</SelectItem>
              <SelectItem value="title3" onMouseDown={(e) => { e.preventDefault(); toggleBlockType('header-three'); }}
            className="font-extrabold" style={{fontSize: '16px'}}>Title 3</SelectItem>
              <SelectItem value="title4" onMouseDown={(e) => { e.preventDefault(); toggleBlockType('header-four'); }}
            className="font-extrabold" style={{fontSize: '14px'}}>Title 4</SelectItem>
              <SelectItem value="title5" onMouseDown={(e) => { e.preventDefault(); toggleBlockType('header-five'); }}
            className="font-extrabold" style={{fontSize: '12px'}}>Title 5</SelectItem>
              <SelectItem value="title6" onMouseDown={(e) => { e.preventDefault(); toggleBlockType('header-six'); }}
            className="font-extrabold" style={{fontSize: '10px'}}>Title 6</SelectItem>
          </SelectContent>
          </Select>

          {/* Selector for font family */}
          {/* <Select>
          <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="System" />
          </SelectTrigger>
          <SelectContent>
              <SelectItem value="font1" onMouseDown={(e) => { e.preventDefault(); toggleBlockFont('arial'); }}
            className="font-normal arial" style={{fontSize: '16px', fontFamily: 'Arial'}}> Arial</SelectItem>
              <SelectItem value="font2" onMouseDown={(e) => { e.preventDefault();  toggleBlockFont('arial');  }}
            className="font-normal" style={{fontSize: '16px', fontFamily: 'Times New Roman'}}>Times New Roman</SelectItem>
              <SelectItem value="font3" onMouseDown={(e) => { e.preventDefault();  toggleBlockFont('arial');  }}
            className="font-normal font-serif" style={{fontSize: '16px', fontFamily: 'sans-serif'}}>Sans Serif </SelectItem>
          </SelectContent>
          </Select> */}
        </div>
      </div>

      {/* Tool bar for inline styles */}
      <div style={{ margin: '10px' }}>
        <Button itemId="blackType"
          onMouseDown={(e) => { e.preventDefault(); toggleInlineStyle('BOLD'); toggleBoldPressedMode();}}
          variant="ghost" size="icon"  className={`w-[25px] h-[30px] font-bold mr-2 ${pressedBold ? "bg-accent text-accent-foreground" : ""}`}
          >
          B
        </Button>
        <Button
          itemId="italicType"
          onMouseDown={(e) => { e.preventDefault(); toggleInlineStyle('ITALIC'); toggleItalicPressedMode();}}
          variant="ghost" size="icon"  className={`w-[25px] h-[30px] font-bold mr-2 ${pressedItalic ? "bg-accent text-accent-foreground" : ""}`}
          >
          I
        </Button>
        <Button itemId="subType"
          onMouseDown={(e) => { e.preventDefault(); toggleInlineStyle('UNDERLINE');  toggleUnderlinePressedMode();}}
          variant="ghost" size="icon"  className={`w-[25px] h-[30px] font-bold mr-2 ${pressedUnderline ? "bg-accent text-accent-foreground" : ""}`}
          >
          U
        </Button>
        <Button itemId=""
          onMouseDown={(e) => { e.preventDefault();  toggleAlignment('left'); toggleAlignLPressedMode();}}
          variant="ghost" size="icon" className={`w-[100px] h-[30px] font-bold mr-5 ${pressedAlignL ? "bg-accent text-accent-foreground" : ""}`}
          >
          Align-L
        </Button>
        <Button itemId=""
          onMouseDown={(e) => { e.preventDefault();  toggleAlignment('center'); toggleAlignMPressedMode(); }}
          variant="ghost" size="icon" className={`w-[100px] h-[30px] font-bold mr-5 ${pressedAlignM ? "bg-accent text-accent-foreground" : ""}`}
        >
          Align-M
        </Button>
        <Button itemId=""
          onMouseDown={(e) => { e.preventDefault();  toggleAlignment('right'); toggleAlignRPressedMode();}}
          variant="ghost" size="icon" className={`w-[100px] h-[30px] font-bold mr-5 ${pressedAlignR ? "bg-accent text-accent-foreground" : ""}`}
        >
          Align-R
        </Button>
      </div>
      <div className='ml-[10px]'>
        <Button
            onMouseDown={(e) => { e.preventDefault(); toggleBlockType('unordered-list-item'); }}
            variant="ghost" size="icon" className="w-[35px] h-[35px] font-bold mr-2"
          >
            <img className="h-8 w-10" src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAAAXNSR0IArs4c6QAAAGRJREFUSEvtkjsKACAMQyN4/ws76OIHQUgKFkHaOTRpXhOcJznvRxjQhp9UVHusYV4AZBp1CbbQpwvcDQxhufQJAx7LoFAYDCbqWjPk6wZqUkkXkE81BWTpeabozy+ydUDU7hU1ApwMGeTiyUAAAAAASUVORK5CYII="/>
        </Button>
          <Button
            onMouseDown={(e) => { e.preventDefault(); toggleBlockType('ordered-list-item'); }}
            variant="ghost" size="icon" className="w-[35px] h-[35px] font-bold mb-4"
          >
            <img className="h-8 w-10" src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAAAXNSR0IArs4c6QAAAM9JREFUSEvt0zFqQkEUheHvLSJiVhDQNdhZWKXMEsQlWNimiZAyjYi1vWDAXrAOadyCmC0kGXjFezAyqG+w8TbTzD3/zDn3FjJXkVnfHZB0OGbRI9YY4zOpkLgQA7xghLcK4PcMUE3zVMgLLHMCnnDAzxkvj169yZj2McErNjl+EABtPCMEHqrRkHt4xwDHHIAPdLDHMIdF12rW+mNT1MUDvioWXQyNAaZoYfd/BrsaDzkIzvFdht04YFVu8Razi70pG2+yydc+OjlFd0DNgT9uTB4ZyR54WAAAAABJRU5ErkJggg=="/>           
          </Button>
      </div>

      {/* Editor's area */}
      <div className='border-dashed border-2' style={{
        minHeight: '1000px',
        padding: '5px',
        borderRadius: '10px',
      }}>
        <Editor
        itemId="editor"
        editorState={editorState}
        onChange={setEditorState}
        handleKeyCommand={handleKeyCommand}
        blockRendererFn={blockRenderer}
        blockStyleFn={blockStyleFn} />
      </div>

      {/* Generate file button */}
      <Button id="generateFile" variant="default" size="icon" className="w-[180px] mt-3 left-0 z-10" onClick={generateDocument}>
        <p className="mt-3 mr-1">Generate file </p>
      </Button>  
    </div>
  );
};